import {
	Box,
	Heading,
	Text,
	VStack,
	Divider,
	Input,
	InputGroup,
	InputLeftElement,
	Icon,
	Link,
	HStack,
	IconButton,
	useDisclosure
} from "@chakra-ui/react";
import FullScreenSection from "./FullScreenSection";
import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { FaSearch, FaPhoneAlt, FaComments } from "react-icons/fa";
import LiveChat from "./LiveChat";
import DownloadApp from "./DownloadApp";

const resourceSections = [
	{
		id: "getting-started",
		title: "Getting started with Swiftchang",
		description: "Create your account, verify your identity, and make your first transfer.",
		body:
			"Create your Swiftchang account, complete identity verification, and set up your profile. Once verified, you can add a beneficiary and make your first transfer in minutes."
	},
	{
		id: "transfer-limits-fees",
		title: "Transfer limits and fees",
		description: "Learn about limits, supported currencies, and fee-free transfers.",
		body:
			"Swiftchang offers transparent pricing with zero transfer fees. Limits depend on your verification level and destination corridor—check your app for personalized limits and requirements."
	},
	{
		id: "tracking-transfer",
		title: "Tracking your transfer",
		description: "See transfer status updates and estimated delivery times.",
		body:
			"Track every transfer in real time from your dashboard. You’ll see status updates, estimated delivery times, and confirmations once funds are received."
	},
	{
		id: "multi-currency-wallet",
		title: "Multi-currency wallet",
		description: "Hold, convert, and manage multiple currencies in one place.",
		body:
			"Hold, convert, and manage multiple currencies in one wallet. Swap currencies instantly and send money using competitive real-time rates."
	},
	{
		id: "security-compliance",
		title: "Security and compliance",
		description: "Understand how we protect your data and stay compliant.",
		body:
			"We use bank‑grade encryption, strict data protection practices, and regulatory compliance checks to safeguard your account and transfers."
	},
	{
		id: "where-can-i-send-and-receive-money",
		title: "Where can I send and receive money with Swiftchang?",
		description: "Supported countries and where Swiftchang is available.",
		body:
			"You can transfer money with Swiftchang to multiple countries around the world including the US, UK, Canada, and Nigeria. We are expanding to more countries, so please check our website regularly for updated information and follow us on Social Media."
	},
	{
		id: "how-do-i-sign-up-for-swiftchang",
		title: "How do I sign up for Swiftchang?",
		description: "Step-by-step setup to get your account ready.",
		body: `Getting started with Swiftchang is quick and simple:

1. Download the app from Google Play or the App Store.

2. Create your free account using your email or phone number.

3. Create your password.

Once you're set up, you can enjoy competitive exchange rates, a secure platform, and fast delivery options for all your international transfers.`
	},
	{
		id: "how-does-swiftchang-work",
		title: "How does Swiftchang work?",
		description: "How to fund, send, and track a transfer.",
		body:
			"After downloading the app and signing up for free with your email or phone number, choose the currency you want to transfer from and add money to the account. Choose the recipient's country and enter their details. Confirm the transfer and track its progress in the app."
	},
	{
		id: "how-long-does-a-money-transfer-usually-take",
		title: "How long does a money transfer usually take?",
		description: "Typical delivery times and instant options.",
		body:
			"The delivery speed varies based on the recipient’s bank. However, most transfers deliver within minutes. For transfers between Swiftchang users or wallet-to-wallet currency conversion, delivery is instant."
	},
	{
		id: "what-are-the-fees-for-using-swiftchang",
		title: "What are the fees for using Swiftchang?",
		description: "Overview of fees and pricing.",
		body: "Swiftchang charges zero fees for all transactions."
	},
	{
		id: "why-should-i-trust-swiftchang",
		title: "Why should I trust Swiftchang?",
		description: "How we secure transfers and protect customers.",
		body: `Every time you choose Swiftchang, you're trusting us with your promises to loved ones. That trust drives us to continuously enhance our security and maintain the highest standards for your transfers.

Swiftchang is fully licensed and regulated in every country we serve, adhering to stringent security and customer protection standards set by our regulatory authorities.

Swiftchang uses bank-level encryption, advanced technology, & predictive tools detect suspicious activity before it becomes a problem. We notify you instantly of any profile updates, ensuring you can verify that all changes were authorized by you.`
	},
	{
		id: "how-much-money-can-i-send-per-transfer",
		title: "How much money can I send per transfer?",
		description: "Limits by verification level and how to check them.",
		body: `The maximum amount you can send per transfer depends on several factors, including your account verification level and the recipient's country. Please check your profile for specific limits based on your situation.

To view your transfer limits:

1. Open the app and tap the menu icon in the top-left corner
2. Select "Account Information" from the settings menu
3. Click "Account Limits"
4. Choose your desired currency to view applicable limits

Note: Currency conversions between wallets count toward your transaction limits. Limits reset daily at 12 AM, weekly on Sundays at 12 AM, and monthly on the first day of each month (UTC timezone).`
	}
];

export default function Resources() {
	const location = useLocation();
	const [query, setQuery] = useState("");
	const [activeId, setActiveId] = useState("");
	const { isOpen, onOpen, onClose } = useDisclosure();

		useEffect(() => {
			if (!location.hash) return;
			const id = location.hash.replace("#", "");
			setActiveId(id);
		}, [location.hash]);

		const searchResults = useMemo(() => {
			if (!query.trim()) return [];
			const lowerQuery = query.toLowerCase();
			return resourceSections.filter((section) =>
				`${section.title} ${section.description}`.toLowerCase().includes(lowerQuery)
			);
		}, [query]);

		const visibleSections = useMemo(() => {
			if (activeId) {
				return resourceSections.filter((section) => section.id === activeId);
			}
			return [];
		}, [activeId]);

		return (
        <VStack spacing={0} width="100%">
            <FullScreenSection
                    justifyContent="center"
                    alignItems="center"
                    isDarkBackground={false}
                    backgroundColor="gray.50"
                    py={100}
            >
                    <VStack spacing={10} width="100%" maxW="1100px" align="stretch">
                        <VStack spacing={3} textAlign="center">
                            <Heading fontSize={{ base: "2.2rem", md: "3rem" }} fontWeight="bold">
                                Resources
                            </Heading>
                            <Text color="gray.600" fontSize="1.1rem">
                                Find quick answers and guidance for common Swiftchang questions.
                            </Text>
                        </VStack>

                        <Box width="100%">
                            <InputGroup size="lg">
                                <InputLeftElement pointerEvents="none">
                                    <Icon as={FaSearch} color="gray.400" />
                                </InputLeftElement>
                                <Input
                                    placeholder="Search for your question"
                                    bg="white"
                                    value={query}
                                    onChange={(event) => setQuery(event.target.value)}
                                />
                            </InputGroup>

                            {query.trim() && searchResults.length > 0 && (
                                <Box mt={3} bg="white" borderRadius="lg" boxShadow="md" p={4} textAlign="left">
                                    <VStack spacing={3} align="stretch">
                                        {searchResults.map((section) => (
                                            <Link
                                                key={section.id}
                                                href="/resources"
                                                color="blue.600"
                                                fontWeight="semibold"
                                                onClick={(event) => {
                                                    event.preventDefault();
                                                    setActiveId(section.id);
                                                    setQuery("");
                                                }}
                                            >
                                                <Text>{section.title}</Text>
                                                <Text fontSize="sm" color="gray.600">
                                                    {section.description}
                                                </Text>
                                            </Link>
                                        ))}
                                    </VStack>
                                </Box>
                            )}

                        </Box>

                        <VStack spacing={10} align="stretch">
                            {visibleSections.map((section) => (
                                <Box key={section.id} id={section.id} bg="white" borderRadius="xl" boxShadow="md" p={{ base: 6, md: 8 }}>
                                    <Heading fontSize={{ base: "1.6rem", md: "2rem" }} mb={3}>
                                        {section.title}
                                    </Heading>
                                    <Text color="gray.600" lineHeight="1.8">
                                        {section.body}
                                    </Text>
                                    <Divider mt={6} />
                                </Box>
                            ))}
                            {visibleSections.length === 0 && (
                                <Box bg="white" borderRadius="xl" boxShadow="md" p={{ base: 6, md: 8 }}>
                                    <Heading fontSize={{ base: "1.4rem", md: "1.6rem" }} mb={2}>
                                        No results found
                                    </Heading>
                                    <Text color="gray.600">Try searching for a different question.</Text>
                                </Box>
                            )}

                            <VStack align="center" spacing={5}>
                                <Heading color="blue.700" fontSize="1.5rem">Can’t find what you’re looking for?</Heading>
                                <VStack align="center" spacing={4}>
                                    <Heading fontSize="1.2rem">Get help 24/7</Heading>
                                    <HStack spacing={7}>
                                        <HStack spacing={3}>
                                            <Icon as={FaPhoneAlt} boxSize={5} color="gray.600" />
                                            <Text fontWeight="semibold">+17802422478</Text>
                                        </HStack>
                                        <VStack align="flex-start" spacing={1}>
                                            <Link href="#" color="gray.600" fontWeight="semibold" onClick={onOpen}>
                                                <HStack spacing={3}>
                                                    <Icon as={FaComments} boxSize={5} />
                                                    <Text whiteSpace="nowrap">Chat</Text>
                                                </HStack>  
                                            </Link>
                                        </VStack>
                                    </HStack>
                                </VStack>
                            </VStack>
                        </VStack>
                    </VStack>
				
								
                <IconButton
                    aria-label="Open live chat"
                    icon={<FaComments size={36} />}
                    position="fixed"
                    bottom={{ base: 5, md: 6 }}
                    right={{ base: 5, md: 6 }}
                    borderRadius="full"
                    bg="black"
                    color="white"
                    boxShadow="lg"
                    boxSize={{ base: "50px", md: "58px" }}
                    _hover={{ bg: "gray.800", transform: "scale(1.05)" }}
                    onClick={onOpen}
                    zIndex="tooltip"
                />
                <LiveChat isOpen={isOpen} onClose={onClose} />
            </FullScreenSection>  
		<FullScreenSection
            justifyContent="center"
            alignItems="center"
            isDarkBackground={false}
            backgroundColor="gray.800"
            py={10}
        >
            <VStack spacing={6} maxW="800px" textAlign="center">
                {/*<Heading fontSize="3rem" fontWeight="bold" color="white">
                    Pre-order the App Now!
                </Heading>*/}
            
                <DownloadApp />
            </VStack>
        </FullScreenSection>  
			
        </VStack>
		);
	}
